import React, {
  createContext,
  useState,
  useEffect,
  useReducer,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import api, { keepSessionAlive, restoreAccessToken } from "../api/api";
import {
  setAccessToken,
  getAccessToken,
  clearAccessToken,
} from "../api/accessTokenStore";
import {
  syncRefreshExpiryFromAuthPayload,
  getLogoutDeadlineMs,
  getMsUntilLogout,
  clearRefreshExpiryMeta,
} from "../api/refreshSessionMeta";
import {
  setRefreshSessionFailedHandler,
  setTwoFaExpiredHandler,
  TWO_FA_EXPIRED_MESSAGE,
} from "../api/authSessionEvents";
import { authState, loginReducer } from "../states/AuthState";
import { dataState, dataReducer } from "../states/DataState";
import Loading from "../components/App/Loading";
import LogoutDialog from "../components/Auth/LogoutDialog";
import SessionExpiredDialog from "../components/Auth/SessionExpiredDialog";
import TwoFaExpiredDialog from "../components/Auth/TwoFaExpiredDialog";
import ServiceErrorDialog from "../components/Auth/ServiceErrorDialog";
import {
  PANEL_COMPANY_MANAGEMENT,
  PANEL_EDITOR_CONTROL,
  SETTING_BILLING,
  SETTING_ORGANIZATION,
  SYSTEM_CONTROL,
  TAX_COMPUTATION,
  PANEL_USER_MANAGEMENT,
} from "../utils/data/controlData";
import { callApiWithResult } from "../utils/callApiWithResult";
import ExpiredAccountDialog from "../components/Auth/ExpiredAccountDialog";
import RefreshTokenExpiringDialog from "../components/Auth/RefreshTokenExpiringDialog";
import CreateOrganizationDialog from "../components/Auth/CreateOrganizationDialog";
import * as patchingTemplateServices from "./services/admin/patchingTemplateServices";
import * as disclosureServices from "./services/disclosure/disclousreServices";
import {
  exchangeIrasToken,
  getInitialIras,
  getInitialIrasTaxAgent,
} from "./services/iras/irasAuthServices";
import { IRAS_CONFIG } from "../config/irasConfig";
import {
  buildIrasOAuthReturnTarget,
  clearIrasOAuthReturnContext,
  isIrasOAuthInFlight,
  persistIrasOAuthState,
  readIrasOAuthReturnLocation,
  readPersistentValue,
  removePersistentValue,
} from "../components/CompanyPage/CorrespondencesTab/irasOAuthHelpers";

const REFRESH_TOKEN_WARN_MS = 5 * 60 * 1000;

function getApiErrorStatus(err) {
  return (
    err?.statusCode ??
    err?.response?.status ??
    err?.error?.statusCode ??
    err?.error?.response?.status ??
    null
  );
}

function storeRequiredAccessToken(payload) {
  const token =
    typeof payload?.accessToken === "string" ? payload.accessToken.trim() : "";

  if (!token) {
    const error = new Error(
      "Authentication response did not include an access token",
    );
    error.statusCode = 502;
    throw error;
  }

  setAccessToken(token);
  return token;
}

function isTwoFaExpired401Error(err) {
  if (!err) return false;
  const code = err.statusCode ?? err.response?.status;
  const msg =
    (typeof err.response?.data?.message === "string"
      ? err.response.data.message
      : null) ??
    (typeof err.message === "string" ? err.message : "") ??
    "";
  return code === 401 && String(msg).includes(TWO_FA_EXPIRED_MESSAGE);
}

const MICROSOFT_SSO_STATE_KEY = "microsoft_sso_state";
const MICROSOFT_SSO_INTENT_KEY = "microsoft_sso_intent";
const MICROSOFT_SSO_REGISTER_DRAFT_KEY = "microsoft_sso_register_draft";
const POST_LOGIN_REDIRECT_FLAG_KEY = "post_login_redirect_pending";
const POST_LOGIN_REDIRECT_URL_KEY = "post_login_redirect_url";
const AUTH_VERIFICATION_SESSION_KEY = "auth_verification_session";

function clearMicrosoftSsoStorage() {
  try {
    sessionStorage.removeItem(MICROSOFT_SSO_STATE_KEY);
    sessionStorage.removeItem(MICROSOFT_SSO_INTENT_KEY);
    sessionStorage.removeItem(MICROSOFT_SSO_REGISTER_DRAFT_KEY);
  } catch {
    /* ignore */
  }
}

function markPostLoginRedirectPending() {
  try {
    sessionStorage.setItem(POST_LOGIN_REDIRECT_FLAG_KEY, "1");
  } catch {
    /* ignore */
  }
}

function setPostLoginRedirectUrl(url) {
  if (!url) return;
  try {
    sessionStorage.setItem(POST_LOGIN_REDIRECT_URL_KEY, url);
  } catch {
    /* ignore */
  }
}

function isPublicAuthPath(pathname) {
  if (!pathname) return false;
  if (pathname === "/password-reset/confirm") return true;
  if (pathname.startsWith("/auth/sso/")) return true;
  if (pathname === "/auth/verification") return true;
  return pathname === "/auth";
}

function isIrasOAuthReturnPath(pathname) {
  return pathname === "/iras/callback";
}

function captureIrasOAuthPostLoginRedirect() {
  if (
    !isIrasOAuthReturnPath(window.location.pathname) &&
    !isIrasOAuthInFlight()
  ) {
    return;
  }
  const target = buildIrasOAuthReturnTarget(
    readIrasOAuthReturnLocation(),
    window.location.search
  );
  setPostLoginRedirectUrl(`${target.pathname}${target.search}${target.hash}`);
  markPostLoginRedirectPending();
}

function writeAuthVerificationSession(payload) {
  try {
    sessionStorage.setItem(
      AUTH_VERIFICATION_SESSION_KEY,
      JSON.stringify(payload)
    );
  } catch {
    /* ignore */
  }
}

function readAuthVerificationSession() {
  try {
    const raw = sessionStorage.getItem(AUTH_VERIFICATION_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function removeAuthVerificationSession() {
  try {
    sessionStorage.removeItem(AUTH_VERIFICATION_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

function updateAuthVerificationSession(patch) {
  const current = readAuthVerificationSession() || {};
  writeAuthVerificationSession({ ...current, ...patch });
}

function mapMicrosoftSsoErrorMessage(raw) {
  if (!raw || typeof raw !== "string") {
    return "Sign-in could not be completed. Please try again.";
  }
  if (raw.includes("Microsoft account is disabled")) {
    return "Your organization account has been disabled. Contact your administrator.";
  }
  if (raw.includes("No local account found. Please register with SSO first.")) {
    return "No account found for this Microsoft identity. Please register with Microsoft first.";
  }
  if (
    raw.includes("This account does not use Microsoft SSO.") ||
    raw.includes("This account is registered with password login")
  ) {
    return "This account uses email and password. Please sign in with your password.";
  }
  if (
    raw.includes("SSO intent mismatch") ||
    raw.includes("Both code and state are required")
  ) {
    return "Sign-in session expired. Please start Microsoft sign-in again.";
  }
  return raw;
}

function extractApiErrorMessage(e) {
  const responseMessage =
    e?.response?.data?.message ?? e?.error?.response?.data?.message;
  if (typeof responseMessage === "string" && responseMessage.trim()) {
    return responseMessage;
  }
  const directMessage = e?.message ?? e?.error?.message;
  return typeof directMessage === "string" ? directMessage : "";
}

export const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isTwoFaSetupPending, setIsTwoFaSetupPending] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [sessionExpiredDialogOpen, setSessionExpiredDialogOpen] =
    useState(false);
  const [twoFaExpiredDialogOpen, setTwoFaExpiredDialogOpen] = useState(false);
  const [serviceErrorDialogOpen, setServiceErrorDialogOpen] = useState(false);
  const [expiredAccountDialogOpen, setExpiredAccountDialogOpen] =
    useState(false);
  const [refreshExpiringDialogOpen, setRefreshExpiringDialogOpen] =
    useState(false);
  const [refreshExpiringMinutesLeft, setRefreshExpiringMinutesLeft] =
    useState(null);
  const suppressSessionWarningAckRef = useRef(false);
  const lastSeenLogoutDeadlineMsRef = useRef(null);
  const [global_authState, authDispatch] = useReducer(loginReducer, authState);
  const [global_dataState, dataDispatch] = useReducer(dataReducer, dataState);
  const [registeredCompanyRole, setRegisteredCompanyRole] = useState(null);
  const [createOrgDialogOpen, setCreateOrgDialogOpen] = useState(false);
  const [createOrgLoading, setCreateOrgLoading] = useState(false);
  const [organizationData, setOrganizationData] = useState(null);
  const [authBootstrapComplete, setAuthBootstrapComplete] = useState(false);

  const registerCompanyRole = useCallback((role, companyId = null) => {
    setRegisteredCompanyRole({
      role: role ?? null,
      companyId: companyId ?? null,
    });
  }, []);

  const clearCompanyRole = useCallback(() => {
    setRegisteredCompanyRole(null);
  }, []);

  const currentCompanyRole =
    registeredCompanyRole && typeof registeredCompanyRole === "object"
      ? registeredCompanyRole.role
      : registeredCompanyRole;

  const redirectToAuthVerification = (type) => {
    window.location.replace(`/auth/verification?type=${type}`);
  };

  const startAuthVerification = (payload) => {
    writeAuthVerificationSession(payload);
    setIsTwoFaSetupPending(payload?.type === "setup");
    redirectToAuthVerification(payload?.type || "login");
  };

  const showQRCodeDialog = (isSend, email) => {
    setIsTwoFaSetupPending(true);
    writeAuthVerificationSession({
      type: "setup",
      isSendEmail: isSend,
      email,
    });
    redirectToAuthVerification("setup");
  };

  const showLoginQRCodeDialog = (payload) => {
    startAuthVerification({
      type: "login",
      user: payload?.user,
      challengeToken: payload?.challengeToken,
    });
  };

  const register = async (values) => {
    try {
      const res = await api.post("/auth/register", values);
      return res.data;
    } catch (error) {
      console.error("Registration failed:", error);
      throw error?.error || error;
    }
  };

  const verifyUser = async (values, axiosConfig = {}) => {
    try {
      const res = await api.post("/auth/verify", values, axiosConfig);
      storeRequiredAccessToken(res.data);
      syncRefreshExpiryFromAuthPayload(res.data);
      markPostLoginRedirectPending();
      authDispatch({ type: "LOGIN", ...res.data.user });
      navigate("/auth?request_type=login", { replace: true });
      window.location.reload();
      return res.data;
    } catch (error) {
      console.error("Registration failed:", error);
      throw error?.error || error;
    }
  };

  const removeRootUser = async (values) => {
    return await callApiWithResult(api.post("/auth/remove-account", values));
  };

  const registerSubUser = async (values) => {
    return await callApiWithResult(
      api.post("/sub-user/registerSubUser", values)
    );
  };

  const registerLocalSubUser = async (values) => {
    return await callApiWithResult(
      api.post("/sub-user/registerLocalSubUser", values)
    );
  };

  const registerSubsidiaryUser = async (values) => {
    return await callApiWithResult(api.post("/subsidiary/register", values));
  };

  const verifySubsidiaryUser = async (values, axiosConfig = {}) => {
    return await callApiWithResult(
      api.post("/subsidiary/verify", values, axiosConfig)
    );
  };

  const convertSubUsertoSubsidiary = async (values) => {
    return await callApiWithResult(
      api.post("/sub-user/convertToSubsidiary", values)
    );
  };

  const updateSubsidiaryUserInfo = async (values) => {
    return await callApiWithResult(api.post("/subsidiary/update-info", values));
  };

  const updateSubsidiaryUserExpireDate = async (values) => {
    return await callApiWithResult(
      api.post("/subsidiary/update-expire-date", values)
    );
  };

  const terminateSubsidiaryUser = async (uid) => {
    return await callApiWithResult(
      api.post("/subsidiary/update-info", {
        targetId: uid,
        updates: {
          status: "banned",
        },
      })
    );
  };

  const verifySubUser = async (values, axiosConfig = {}) => {
    return await callApiWithResult(
      api.post("/sub-user/verifySubUser", values, axiosConfig)
    );
  };

  const updateSubUserRole = async (values) => {
    return await callApiWithResult(
      api.post("/sub-user/updateSubUserRole", values)
    );
  };

  const updateSubUserName = async (values) => {
    return await callApiWithResult(
      api.post("/sub-user/updateSubUserName", values)
    );
  };

  const removeSubUser = async (values) => {
    return await callApiWithResult(api.post("/sub-user/removeSubUser", values));
  };

  const transferOwnership = async (values) => {
    return await callApiWithResult(
      api.post("/subscription/transferOwner", values)
    );
  };

  const updateUserSubscription = async (values) => {
    return await callApiWithResult(
      api.post("/subscription/updateUserSubscription", values)
    );
  };

  const activeSubscription = async (values) => {
    return await callApiWithResult(
      api.post("/subscription/activeSubscription", values)
    );
  };

  const terminateSubscription = async (values) => {
    return await callApiWithResult(
      api.post("/subscription/terminateSubscription", values)
    );
  };

  const renewSubscription = async (values) => {
    return await callApiWithResult(
      api.post("/subscription/renewSubscription", values)
    );
  };

  const updateSubscriptionDates = async (values) => {
    return await callApiWithResult(
      api.post("/subscription/updateSubscriptionDates", values)
    );
  };

  const resetSubscriptionToTrial = async (values) => {
    return await callApiWithResult(
      api.post("/subscription/resetSubscriptionToTrial", values)
    );
  };

  const chargeSavedCard = async (values) => {
    try {
      const res = await api.post("/payment/chargeSavedCard", values);
      //window.location.reload();
      return res.data;
    } catch (error) {
      console.error("chargeSavedCard failed:", error);
      throw error.error;
    }
  };

  const getAllUserWithSubscription = async () => {
    try {
      const res = await api.get("/subscription/getAllUserWithSubscription");
      return res.data;
    } catch (error) {
      console.error("getAllUserWithSubscription failed:", error);
      throw error.error;
    }
  };

  const getPlans = async () => {
    try {
      const res = await api.get("/subscription/getPlans");
      return res.data;
    } catch (error) {
      console.error("getPlans failed:", error);
      throw error.error;
    }
  };

  const getPaymentBySubscriptionId = async () => {
    try {
      const res = await api.get("/subscription/getPayment");
      return res.data; // Assuming you want to return the data part of the response
    } catch (error) {
      console.error("getPayment Error:", error);
      throw error.error;
    }
  };

  const getNextBillingPayment = async () => {
    try {
      const res = await api.get("/subscription/getNextBillingPayment");
      return res.data; // Assuming you want to return the data part of the response
    } catch (error) {
      console.error("getNextBillingPayment Error:", error);
      throw error.error;
    }
  };

  const getSubscriptionChangeLog = async () => {
    try {
      const res = await api.get("/subscription/getSubscriptionChangeLog");
      return res.data; // Assuming you want to return the data part of the response
    } catch (error) {
      console.error("getSubscriptionChangeLog Error:", error);
      throw error.error;
    }
  };

  const createCompany = async (values) => {
    try {
      const res = await api.post("/user/createCompany", values);
      return res.data;
    } catch (error) {
      console.error("createCompany failed:", error);
      throw error.error;
    }
  };

  const deleteCompany = async (values) => {
    try {
      const res = await api.post("/user/deleteCompany", values);
      window.location.reload();
      return res.data;
    } catch (error) {
      console.error("deleteCompany failed:", error);
      throw error.error;
    }
  };

  const tempDeleteCompany = async (values) => {
    try {
      const res = await api.post("/user/tempDeleteCompany", values);
      return res.data;
    } catch (error) {
      console.error("tempDeleteCompany failed:", error);
      throw error.error;
    }
  };

  const restoreCompany = async (values) => {
    try {
      const res = await api.post("/user/restoreDeletedCompany", values);
      window.location.reload();
      return res.data;
    } catch (error) {
      console.error("restoreCompany failed:", error);
      throw error.error;
    }
  };

  const getCompanyRole = useCallback(async (values) => {
    try {
      const res = await api.get(`/user/companyRole`, { params: values });
      return res.data;
    } catch (error) {
      console.error("getCompanyRole Error:", error);
      throw error.error;
    }
  }, []);

  const updateCaSettings = async (values) => {
    try {
      const res = await api.post("/user/manageCaSettings", values);
      //window.location.reload();
      return res.data;
    } catch (error) {
      console.error("updateCaSettings failed:", error);
      throw error.error;
    }
  };

  const createTaxComputation = async (values) => {
    try {
      const res = await api.post("/docs/createTaxComputation", values);
      //window.location.reload();
      return res.data;
    } catch (error) {
      console.error("createTaxComputation failed:", error);
      throw error.error;
    }
  };

  const updateTaxComputation = async (values) => {
    try {
      const res = await api.post("/docs/updateTaxComputation", values);
      //window.location.reload();
      return res.data;
    } catch (error) {
      console.error("updateTaxComputation failed:", error);
      throw error.error;
    }
  };

  const deleteTaxComputation = async (values) => {
    try {
      const res = await api.post("/docs/deleteTaxComputation", values);
      return res.data;
    } catch (error) {
      console.error("deleteTaxComputation failed:", error);
      throw error.error;
    }
  };

  const reorderScheduleNo = async (values) => {
    try {
      const res = await api.post("/docs/reorderPLScheduleNo", values);
      //window.location.reload();
      return res.data;
    } catch (error) {
      console.error("reorderScheduleNo failed:", error);
      throw error.error;
    }
  };

  const reorderCAScheduleNo = async (values) => {
    try {
      const res = await api.post("/docs/reorderCAScheduleNo", values);
      //window.location.reload();
      return res.data;
    } catch (error) {
      console.error("reorderCAScheduleNo failed:", error);
      throw error.error;
    }
  };

  const updateDetailsPL = async (values) => {
    try {
      const res = await api.post("/docs/updateDetailsPL", values);
      // resetData("detailPL");
      //window.location.reload();
      return res.data;
    } catch (error) {
      console.error("updateDetailsPL failed:", error);
      throw error.error;
    }
  };

  const updateMainComputation = async (values) => {
    try {
      const res = await api.post("/docs/updateMainComputation", values);
      //window.location.reload();
      return res.data;
    } catch (error) {
      console.error("updateMainComputation failed:", error);
      throw error.error;
    }
  };

  const rollOverTaxComputation = async (values) => {
    try {
      const res = await api.post("/docs/rollOverTaxComputation", values);
      return res.data;
    } catch (error) {
      console.error("rollOverTaxComputation failed:", error);
      throw error.error;
    }
  };

  const updateCompany = async (values) => {
    try {
      const res = await api.post("/user/updateCompany", values);
      return res.data;
    } catch (error) {
      console.error("updateCompany failed:", error);
      throw error.error;
    }
  };

  const getCompanyList = async (status = "active") => {
    //setIsLoading(true);
    try {
      const res = await api.get("/data/companies", {
        params: status ? { status } : {},
      });
      //setIsLoading(false);
      return res.data;
    } catch (error) {
      console.error("Retrieve Company Error:", error);
      //setIsLoading(false);
      throw error.error;
    }
  };

  const getTaxComputationList = useCallback(async (c_id, { skipMasExchangeRate = false, signal } = {}) => {
    //setIsLoading(true);
    try {
      const res = await api.get(`/data/tax-computations/${c_id}`, {
        params: skipMasExchangeRate ? { skipMasExchangeRate: true } : undefined,
        signal,
      });
      //setIsLoading(false);
      return res.data; // Assuming you want to return the data part of the response
    } catch (error) {
      if (signal?.aborted) throw error;
      console.error("Retrieve Details P&L List Error:", error);
      //setIsLoading(false);
      throw error.error;
    }
  }, []);

  const getDetailsPLData = useCallback(async (tc_id) => {
    //setIsLoading(true);
    try {
      const res = await api.get(`/data/tax-computations/details-pl/${tc_id}`);
      //setIsLoading(false);
      return res.data; // Assuming you want to return the data part of the response
    } catch (error) {
      console.error("Retrieve detailsPLData Error:", error);
      //setIsLoading(false);
      throw error.error;
    }
  }, []);

  const getMainComputationData = async (tc_id) => {
    try {
      const res = await api.get(`/data/tax-computations/main-comp/${tc_id}`);
      return res.data;
    } catch (error) {
      console.error("Retrieve mainComputationDetails Error:", error);
      throw error.error;
    }
  };

  const getMainCompDocOnly = useCallback(async (tc_id) => {
    try {
      const res = await api.get(
        `/data/tax-computations/main-comp-doc/${tc_id}`
      );
      return res.data;
    } catch (error) {
      console.error("Retrieve main comp doc snapshot Error:", error);
      throw error.error;
    }
  }, []);

  const getStatementDoc = async (tc_id) => {
    try {
      const res = await api.get(`/data/tax-computations/docs/${tc_id}`);
      return res.data;
    } catch (error) {
      console.error("Retrieve mainComputation docs Error:", error);
      throw error.error;
    }
  };

  const getCASettings = async () => {
    //setIsLoading(true);
    try {
      const res = await api.get("/data/ca-settings");
      //setIsLoading(false);
      return res.data; // Assuming you want to return the data part of the response
    } catch (error) {
      console.error("Retrieve CA Settings Error:", error);
      //setIsLoading(false);
      throw error;
    }
  };

  const getSchedules = async (tc_id, schedule_type = {}) => {
    //setIsLoading(true);
    try {
      const res = await api.get(`/data/pl-schedules/${tc_id}`, {
        params: schedule_type ? { schedule_type } : {},
      });
      //setIsLoading(false);
      return res.data; // Assuming you want to return the data part of the response
    } catch (error) {
      console.error("Retrieve Schedule List Error:", error);
      //setIsLoading(false);
      throw error.error;
    }
  };

  const getSchedulesWithSavedDoc = async (tc_id, schedule_type = {}) => {
    try {
      const res = await api.get(`/data/pl-schedules/withDoc/${tc_id}`, {
        params: schedule_type ? { schedule_type } : {},
      });
      return res.data;
    } catch (error) {
      console.error("Retrieve Schedule List with Doc Error:", error);
      throw error.error;
    }
  };

  const getCaSchedulesWithSavedDoc = async (tc_id, schedule_type = {}) => {
    try {
      const res = await api.get(`/data/ca-schedules/withDoc/${tc_id}`, {
        params: schedule_type ? { schedule_type } : {},
      });
      return res.data;
    } catch (error) {
      console.error("Retrieve Schedule List with Doc Error:", error);
      throw error.error;
    }
  };

  const getScheduleData = async (sch_id, schedule_type = undefined) => {
    //setIsLoading(true);
    try {
      const res = await api.get(`/data/pl-schedule/${sch_id}`, {
        params: schedule_type ? { schedule_type } : {},
      });
      //setIsLoading(false);
      return res.data; // Assuming you want to return the data part of the response
    } catch (error) {
      console.error("Retrieve Schedule Data Error:", error);
      //setIsLoading(false);
      throw error.error;
    }
  };

  const createPLSchedule = async (values) => {
    try {
      const res = await api.post("/docs/createPLSchedule", values);
      //window.location.reload();
      return res;
    } catch (error) {
      console.error("create PL Schedule failed:", error);
      throw error.error;
    }
  };

  const updatePLSchedule = async (values) => {
    try {
      const res = await api.post("/docs/updatePLSchedule", values);
      //window.location.reload();
      return res.data;
    } catch (error) {
      console.error("update PL Schedule failed:", error);
      throw error.error;
    }
  };

  const addAutoloadData = async (values) => {
    try {
      const res = await api.post("/docs/addAutoloadData", values);
      return res.data;
    } catch (error) {
      console.error("addAutoloadData failed:", error);
      throw error.error;
    }
  };

  const editAutoloadData = async (values) => {
    try {
      const res = await api.post("/docs/editAutoloadData", values);
      return res.data;
    } catch (error) {
      console.error("editAutoloadData failed:", error);
      throw error.error;
    }
  };

  const deleteAutoloadData = async (values) => {
    try {
      const res = await api.post("/docs/deleteAutoloadData", values);
      return res.data;
    } catch (error) {
      console.error("deleteAutoloadData failed:", error);
      throw error.error;
    }
  };

  const createCASchedule = async (values) => {
    return await callApiWithResult(api.post("/docs/createCASchedule", values));
  };

  const updateCASchedule = async (values) => {
    return await callApiWithResult(api.post("/docs/updateCASchedule", values));
  };

  const deleteCAScheduleByType = async (values) => {
    return await callApiWithResult(api.post("/docs/deleteCASchedule", values));
  };

  const getCAScheduleList = async (tc_id, schedule_type = {}) => {
    return await callApiWithResult(
      api.get(`/data/ca-schedules/${tc_id}`, {
        params: schedule_type ? { schedule_type } : {},
      })
    );
  };

  const getCAScheduleDataByType = async (sch_id, schedule_type = undefined) => {
    //return await callApiWithResult(api.get(`/data/ca-schedule/${sch_id}`));
    try {
      const res = await api.get(`/data/ca-schedule/${sch_id}`, {
        params: schedule_type ? { schedule_type } : {},
      });
      //setIsLoading(false);
      return res.data; // Assuming you want to return the data part of the response
    } catch (error) {
      console.error("Retrieve Schedule Data Error:", error);
      //setIsLoading(false);
      throw error.error;
    }
  };

  const getCASummaryPDFDetails = async (tc_id) => {
    return await callApiWithResult(api.get(`/data/ca-summary/pdf/${tc_id}`));
  };

  const getSubUserList = async () => {
    return await callApiWithResult(api.get("/data/sub-users"));
  };

  const getSubsidiaryUserList = async () => {
    return await callApiWithResult(api.get("/data/subsidiaries"));
  };

  const getSubUserListByUID = async (uid) => {
    return await callApiWithResult(api.get(`/data/sub-users/${uid}`));
  };

  const getSubsidiaryUserListByUID = async (uid) => {
    return await callApiWithResult(api.get(`/data/subsidiaries/${uid}`));
  };

  const resendVerificationEmail = async (values) => {
    try {
      const res = await api.post(`/auth/resend-verification-email`, {
        email: values,
      });
      return res.data;
    } catch (error) {
      console.error("resendVerificationEmail:", error);
      throw error;
    }
  };

  const resendSubsidiaryVerificationEmail = async (value) => {
    return await callApiWithResult(
      api.post("/subsidiary/resent-verification-email", { email: value })
    );
  };

  const resendSubsidiaryAuthEmail = async (values) => {
    return await callApiWithResult(
      api.post("/auth/google-auth-trigger-root", values)
    );
  };

  const google_auth = async () => {
    try {
      const res = await api.post("/auth/google-auth");
      return res.data;
    } catch (error) {
      console.error("Google Auth failed:", error);
      throw error.error;
    }
  };

  const google_auth_challenge = async (challengeToken) => {
    try {
      const res = await api.post("/auth/google-auth/challenge", {
        challengeToken,
      });
      return res.data;
    } catch (error) {
      console.error("Google Auth challenge failed:", error);
      throw error.error;
    }
  };

  const reset_google_auth = async () => {
    try {
      const res = await api.post("/auth/google-auth-reset");
      return res.data;
    } catch (error) {
      console.error("Reset Google Auth failed:", error);
      throw error.error;
    }
  };

  const reset_google_auth_challenge = async (challengeToken) => {
    try {
      const res = await api.post("/auth/google-auth-reset/challenge", {
        challengeToken,
      });
      return res.data;
    } catch (error) {
      console.error("Reset Google Auth challenge failed:", error);
      throw error.error;
    }
  };

  const verify_login = async (values) => {
    try {
      const res = await api.post("/auth/verify-login", values);
      storeRequiredAccessToken(res.data);
      syncRefreshExpiryFromAuthPayload(res.data);
      removeAuthVerificationSession();
      markPostLoginRedirectPending();
      authDispatch({ type: "LOGIN", ...res.data.user });
      window.location.replace("/consolePanel");
      return true;
    } catch (error) {
      console.error("Verify Google Auth failed:", error);
      throw error?.error || error;
    }
  };

  const verify_google_auth = async (values) => {
    try {
      const res = await api.post("/auth/google-verify", values);
      storeRequiredAccessToken(res.data);
      syncRefreshExpiryFromAuthPayload(res.data);
      setIsTwoFaSetupPending(false);
      removeAuthVerificationSession();
      markPostLoginRedirectPending();
      window.location.replace("/consolePanel");
      return res.data;
    } catch (error) {
      console.error("Verify Google Auth failed:", error);
      throw error?.error || error;
    }
  };

  const refresh_google_auth = async () => {
    try {
      const res = await api.post("/auth/refresh-google-authenticate");
      return res.data;
    } catch (error) {
      console.error("refresh-google-authenticate failed:", error);
      throw error.error;
    }
  };

  const getUserData = async (uid) => {
    try {
      const res = await api.get(`/data/user/${uid}`);
      return res.data; // Assuming you want to return the data part of the response
    } catch (error) {
      console.error("Retrieve User Error:", error);
      throw error.error;
    }
  };

  const updateUserProfile = async (values) => {
    try {
      const res = await api.post("/auth/update-profile", values);
      return res;
    } catch (error) {
      console.error("updateUserProfile failed:", error);
      throw error.error;
    }
  };

  const getCompanyListByUID = async (uid) => {
    try {
      const res = await api.get(`/data/companies/${uid}`);
      return res.data; // Assuming you want to return the data part of the response
    } catch (error) {
      console.error("Retrieve Companies Error:", error);
      throw error.error;
    }
  };

  const deleteScheduleByScheduleType = async (values) => {
    try {
      const res = await api.post("/docs/deletePLSchedule", values);
      //window.location.reload();
      return res;
    } catch (error) {
      console.error("deleteScheduleByScheduleType failed:", error);
      throw error.error;
    }
  };

  const removeRowFromSchedule = async (values) => {
    try {
      const res = await api.post("/user/removeRowFromSchedule", values);
      //window.location.reload();
      return res;
    } catch (error) {
      console.error("removeRowFromSchedule failed:", error);
      throw error.error;
    }
  };

  const login = async (values) => {
    try {
      const res = await api.post("/auth/login", values);
      const needs2fa =
        res.data.is2faRequired === true ||
        res.data.isLoginVerifyRequired === true;
      if (needs2fa) {
        clearAccessToken();
        clearRefreshExpiryMeta();

        if (res.data.reason === "2FA setup is incomplete") {
          startAuthVerification({
            type: "setup",
            user: res.data.user,
            email: res.data.user?.email,
            challengeToken: res.data.challengeToken,
          });
          return res.data;
        }

        startAuthVerification({
          type: "login",
          user: res.data.user,
          challengeToken: res.data.challengeToken,
        });
        return res.data;
      }
      storeRequiredAccessToken(res.data);
      syncRefreshExpiryFromAuthPayload(res.data);
      try {
        localStorage.removeItem("refreshToken");
      } catch {
        /* ignore */
      }
      markPostLoginRedirectPending();
      authDispatch({ type: "LOGIN", ...res.data.user });
      window.location.reload();
      return res.data;
    } catch (error) {
      console.error("Login failed:", error);
      throw error?.error || error;
    }
  };

  const applyMicrosoftAuthSuccess = (authPayload) => {
    storeRequiredAccessToken(authPayload);
    syncRefreshExpiryFromAuthPayload(authPayload);
    try {
      localStorage.removeItem("refreshToken");
    } catch {
      /* ignore */
    }
    authDispatch({ type: "LOGIN", ...authPayload.user });
    clearMicrosoftSsoStorage();
    window.location.replace("/consolePanel");
  };

  const startMicrosoftSSO = async (intent, registerDraft = null) => {
    const res = await api.get("/auth/sso/microsoft/start", {
      params: { intent },
    });
    const body = res?.data ?? res;
    const payload = body?.data ?? body;
    const authorizeUrl = payload?.authorizeUrl;
    const state = payload?.state;
    if (!authorizeUrl || !state) {
      throw new Error("Could not start Microsoft sign-in.");
    }
    sessionStorage.setItem(MICROSOFT_SSO_STATE_KEY, state);
    sessionStorage.setItem(MICROSOFT_SSO_INTENT_KEY, intent);
    if (intent === "register" && registerDraft) {
      sessionStorage.setItem(
        MICROSOFT_SSO_REGISTER_DRAFT_KEY,
        JSON.stringify(registerDraft)
      );
    } else {
      sessionStorage.removeItem(MICROSOFT_SSO_REGISTER_DRAFT_KEY);
    }
    console.debug("[MS SSO] start state stored", {
      intent,
      state,
      hasRegisterDraft: Boolean(intent === "register" && registerDraft),
    });
    window.location.assign(authorizeUrl);
  };

  const completeMicrosoftSSOCallback = async ({ code, state, error }) => {
    try {
      if (error) {
        if (error === "access_denied") {
          throw new Error("Sign-in was cancelled.");
        }
        throw new Error("Microsoft sign-in failed. Please try again.");
      }
      if (!code || !state) {
        throw new Error(
          "Missing authorization code. Please start Microsoft sign-in again."
        );
      }
      const savedState = sessionStorage.getItem(MICROSOFT_SSO_STATE_KEY);
      console.debug("[MS SSO] callback state check", {
        urlState: state,
        savedState,
      });
      if (state && !savedState) {
        console.warn(
          "[MS SSO] callback found URL state but savedState is empty; possible sessionStorage context loss or callback double-run."
        );
      }
      if (!savedState || state !== savedState) {
        throw new Error(
          "Sign-in session expired or invalid. Please start Microsoft sign-in again."
        );
      }
      const intent =
        sessionStorage.getItem(MICROSOFT_SSO_INTENT_KEY) || "login";

      if (intent === "register") {
        let registerDraft = {};
        try {
          const raw = sessionStorage.getItem(MICROSOFT_SSO_REGISTER_DRAFT_KEY);
          registerDraft = raw ? JSON.parse(raw) : {};
        } catch {
          registerDraft = {};
        }
        if (!registerDraft || typeof registerDraft !== "object") {
          registerDraft = {};
        }
        const res = await api.post("/auth/sso/microsoft/register", {
          code,
          state,
          ...registerDraft,
        });
        const body = res?.data ?? res;
        const authPayload = body?.data ?? body;
        applyMicrosoftAuthSuccess(authPayload);
      } else {
        const res = await api.post("/auth/sso/microsoft/login", {
          code,
          state,
        });
        const body = res?.data ?? res;
        const authPayload = body?.data ?? body;
        applyMicrosoftAuthSuccess(authPayload);
      }
    } catch (e) {
      const raw = extractApiErrorMessage(e);
      clearMicrosoftSsoStorage();
      throw new Error(mapMicrosoftSsoErrorMessage(raw));
    }
  };

  const changePassword = async (values) => {
    try {
      const res = await api.post("/auth/change-password", values);
      //window.location.reload();
      return res.data;
    } catch (error) {
      console.error("change-password failed:", error);
      throw error.error;
    }
  };

  const forgotPassword = async (values) => {
    try {
      const res = await api.post("/auth/forgot-password", values);
      return res.data;
    } catch (error) {
      console.error("forgot-password failed:", error);
      throw error.error;
    }
  };

  const resetPassword = async (values) => {
    try {
      const res = await api.post("/auth/reset-password", values);
      //window.location.reload();
      return res.data;
    } catch (error) {
      console.error("reset-password failed:", error);
      throw error.error;
    }
  };

  const clearIrasCredentials = () => {
    localStorage.removeItem(IRAS_CONFIG.SECURITY_CONFIG.TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(
      IRAS_CONFIG.SECURITY_CONFIG.SERVER_OAUTH_CONNECTED_KEY
    );
    sessionStorage.removeItem(
      IRAS_CONFIG.SECURITY_CONFIG.SERVER_OAUTH_EXPIRES_AT_KEY
    );
    if (!isIrasOAuthInFlight()) {
      removePersistentValue(IRAS_CONFIG.SECURITY_CONFIG.STATE_STORAGE_KEY);
      clearIrasOAuthReturnContext();
    }
  };

  const clearUserData = async ({ notifyServer = true } = {}) => {
    const token = getAccessToken();

    if (notifyServer && token) {
      try {
        await api.post(
          "/auth/logout",
          {},
          {
            skipAuthRefresh: true,
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } catch (error) {
        console.error("clearUserData failed:", error);
      }
    }

    clearAccessToken();
    clearRefreshExpiryMeta();
    try {
      localStorage.removeItem("refreshToken");
    } catch {
      /* ignore */
    }

    // Clear IRAS credentials
    clearIrasCredentials();

    setIsTwoFaSetupPending(false);
    removeAuthVerificationSession();
    authDispatch({ type: "LOGOUT" });
    dataDispatch({ type: "LOGOUT" });
  };

  const initiateLogout = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogout = async () => {
    setLogoutLoading(true);
    try {
      navigate("/logout", { replace: true });
      setLogoutDialogOpen(false);
      clearIrasOAuthReturnContext();
      await clearUserData();
      setLogoutLoading(false);
      window.location.replace("/auth?request_type=login");
    } catch (error) {
      setLogoutLoading(false);
      setLogoutDialogOpen(false);
      window.location.replace("/auth?request_type=login");
    }
  };

  const switchAccountForVerification = async () => {
    await clearUserData();
    setLogoutDialogOpen(false);
    setLogoutLoading(false);
  };

  const cancelLogout = () => {
    setLogoutDialogOpen(false);
  };

  const setData = useCallback((section, field, value) => {
    dataDispatch({
      type: "UPDATE_DATA",
      section,
      payload:
        field === "all"
          ? { ...value, unsaved: true }
          : { [field]: value, unsaved: true },
    });
  }, []);

  const resetData = useCallback((section) => {
    dataDispatch({
      type: "RESET_DATA",
      section,
    });
  }, []);

  const resetAllData = useCallback(() => {
    dataDispatch({
      type: "RESET_ALL_DATA",
    });
  }, []);

  const handleSessionExpired = () => {
    void clearUserData({ notifyServer: false });
    setSessionExpiredDialogOpen(false);
    window.location.href = "/auth?request_type=login";
  };

  const handleTwoFaExpiredConfirm = () => {
    void clearUserData({ notifyServer: false });
    setTwoFaExpiredDialogOpen(false);
    window.location.href = "/auth?request_type=login";
  };

  const refreshAuthenticatedUser = useCallback(async () => {
    const res = await api.get("/data/user");
    authDispatch({ type: "LOGIN", ...res.data });
    return res.data;
  }, []);

  const isLogin = async () => {
    if (window.location.pathname === "/auth/verification") {
      setAuthBootstrapComplete(true);
      return;
    }

    if (global_authState?.uid || isTwoFaSetupPending) {
      setAuthBootstrapComplete(true);
      return;
    }
    try {
      setIsLoading(true);
      const token = getAccessToken();

      if (token) {
        try {
          const res = await api.get("/data/user");

          if (res.data.status === "inactive") {
            startAuthVerification({
              type: "setup",
              user: res.data,
              email: res.data.email,
            });
          } else {
            authDispatch({ type: "LOGIN", ...res.data });
          }
        } catch (apiError) {
          const status = getApiErrorStatus(apiError);
          if (status >= 500) {
            console.warn("Service unavailable, will retry shortly");
            setServiceErrorDialogOpen(true);
            return;
          }

          if (!isTwoFaExpired401Error(apiError)) {
            captureIrasOAuthPostLoginRedirect();
            if (
              !isPublicAuthPath(window.location.pathname) &&
              !isIrasOAuthReturnPath(window.location.pathname)
            ) {
              setSessionExpiredDialogOpen(true);
            }
          }

          throw apiError.error;
        }
      } else {
        if (!isPublicAuthPath(window.location.pathname)) {
          try {
            // The refresh token is held in an HttpOnly cookie, so recover the
            // access token before treating a missing local token as logout.
            await restoreAccessToken();
            const res = await api.get("/data/user");

            if (res.data.status === "inactive") {
              startAuthVerification({
                type: "setup",
                user: res.data,
                email: res.data.email,
              });
            } else {
              authDispatch({ type: "LOGIN", ...res.data });
            }
            return;
          } catch (restoreError) {
            if (getApiErrorStatus(restoreError) >= 500) {
              console.warn("Service unavailable, will retry shortly");
              setServiceErrorDialogOpen(true);
              return;
            }
          }

          // No valid access/refresh session remains; redirect to auth.
          clearRefreshExpiryMeta();
          try {
            localStorage.removeItem("refreshToken");
          } catch {
            /* ignore */
          }
          captureIrasOAuthPostLoginRedirect();
          const redirectUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
          if (!isIrasOAuthInFlight()) {
            setPostLoginRedirectUrl(redirectUrl);
            markPostLoginRedirectPending();
          }
          window.location.href = "/auth";
        }
      }
    } catch (error) {
      // Handle unexpected errors
      console.error("Unexpected error during login check:", error);

      if (getApiErrorStatus(error) >= 500) {
        setServiceErrorDialogOpen(true);
        return;
      }

      if (
        error?.response?.status === 403 &&
        error?.response?.data?.message ===
          "Forbidden: Subsidiary account expired"
      ) {
        setExpiredAccountDialogOpen(true);
        return;
      }

      captureIrasOAuthPostLoginRedirect();
      await clearUserData({ notifyServer: false });

      if (!isTwoFaExpired401Error(error)) {
        if (
          !isPublicAuthPath(window.location.pathname) &&
          !isIrasOAuthReturnPath(window.location.pathname)
        ) {
          setSessionExpiredDialogOpen(true);
        }
      }
    } finally {
      setIsLoading(false);
      setAuthBootstrapComplete(true);
    }
  };

  const handleDismissRefreshExpiring = useCallback(() => {
    suppressSessionWarningAckRef.current = true;
    setRefreshExpiringDialogOpen(false);
  }, []);

  // Check for organization after user login
  const checkOrganization = useCallback(async () => {
    if (!global_authState?.uid) return;

    // Only check for admin users
    if (
      global_authState.user_type !== "admin" ||
      (global_authState.user_role &&
        String(global_authState.user_role).toLowerCase() !== "admin")
    ) {
      return;
    }

    try {
      const orgData = await getOrganization();
      setOrganizationData(orgData?.organization || null);

      // If organization is null or empty, show dialog
      if (!orgData?.organization) {
        setCreateOrgDialogOpen(true);
      }
    } catch (error) {
      console.error("Error checking organization:", error);
      // On error, also check if we should show the dialog
      if (error?.response?.status !== 404) {
        console.error("Unexpected error checking organization");
      }
    }
  }, [global_authState]);

  useEffect(() => {
    if (!global_authState?.uid) {
      setRefreshExpiringDialogOpen(false);
      setRefreshExpiringMinutesLeft(null);
      suppressSessionWarningAckRef.current = false;
      lastSeenLogoutDeadlineMsRef.current = null;
      return;
    }

    const tick = () => {
      const deadlineMs = getLogoutDeadlineMs();
      if (deadlineMs == null) return;

      if (lastSeenLogoutDeadlineMsRef.current !== deadlineMs) {
        lastSeenLogoutDeadlineMsRef.current = deadlineMs;
        suppressSessionWarningAckRef.current = false;
      }

      const msLeft = getMsUntilLogout();
      if (msLeft == null) return;

      if (msLeft <= 0) {
        setRefreshExpiringDialogOpen(false);
        setRefreshExpiringMinutesLeft(null);
        return;
      }

      if (msLeft > REFRESH_TOKEN_WARN_MS) {
        setRefreshExpiringDialogOpen(false);
        setRefreshExpiringMinutesLeft(null);
        suppressSessionWarningAckRef.current = false;
        return;
      }

      const minutesLeft = Math.max(1, Math.ceil(msLeft / 60_000));
      setRefreshExpiringMinutesLeft(minutesLeft);

      if (suppressSessionWarningAckRef.current) return;
      setRefreshExpiringDialogOpen(true);
    };

    tick();
    const intervalId = setInterval(tick, 10_000);
    const onVisibility = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [global_authState?.uid]);

  useEffect(() => {
    if (!global_authState?.uid) return undefined;

    let cancelled = false;
    let lastAttemptMs = 0;
    const THROTTLE_MS = 20_000;

    const run = () => {
      if (cancelled) return;
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastAttemptMs < THROTTLE_MS) return;
      lastAttemptMs = now;
      void keepSessionAlive();
    };

    run();
    const intervalId = setInterval(run, 30_000);
    const onVisible = () => {
      if (document.visibilityState === "visible") run();
    };
    const onInteract = () => run();

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    window.addEventListener("pointerdown", onInteract, { passive: true });
    window.addEventListener("keydown", onInteract);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      window.removeEventListener("pointerdown", onInteract);
      window.removeEventListener("keydown", onInteract);
    };
  }, [global_authState?.uid]);

  useEffect(() => {
    setRefreshSessionFailedHandler(() => {
      captureIrasOAuthPostLoginRedirect();
      void clearUserData({ notifyServer: false });
      if (
        !isPublicAuthPath(window.location.pathname) &&
        !isIrasOAuthReturnPath(window.location.pathname)
      ) {
        setSessionExpiredDialogOpen(true);
      }
    });
    return () => setRefreshSessionFailedHandler(null);
  }, []);

  useEffect(() => {
    setTwoFaExpiredHandler(() => {
      const path = window.location.pathname;
      if (path !== "/auth" && path !== "/password-reset/confirm") {
        setTwoFaExpiredDialogOpen(true);
      }
    });
    return () => setTwoFaExpiredHandler(null);
  }, []);

  useEffect(() => {
    isLogin();
  }, []);

  // Check organization after user is authenticated
  useEffect(() => {
    if (global_authState?.uid && !isPublicAuthPath(window.location.pathname)) {
      checkOrganization();
    }
  }, [global_authState?.uid]);

  // IRAS Integration Functions
  // IRAS CorpPass Authentication Initiation (backend PKCE + state; redirect_uri from IRAS_REDIRECT_URI)
  const initiateCorpPassAuth = async (callbackUrl, uen) => {
    void callbackUrl;
    try {
      const normalizedUen = uen ? `${uen}`.trim() : "";
      if (!normalizedUen) {
        throw new Error("Company UEN is required to connect to IRAS.");
      }

      const response = await getInitialIras(normalizedUen);
      if (response?.status === "success" && response?.data?.state != null) {
        persistIrasOAuthState(response.data.state);
        return {
          status: "success",
          message: response.message,
          data: {
            auth_url: response.data.authorization_url,
            authorization_url: response.data.authorization_url,
            state: response.data.state,
            expires_at: response.data.expires_at,
          },
        };
      }

      throw new Error(
        response?.message || "Failed to initiate CorpPass authentication"
      );
    } catch (error) {
      console.error("CorpPass authentication initiation failed:", error);
      throw error.error || error;
    }
  };

  const initiateCorpPassTaxAgentAuth = async (callbackUrl, uen) => {
    void callbackUrl;
    try {
      const normalizedUen = uen ? `${uen}`.trim() : "";
      if (!normalizedUen) {
        throw new Error("Company UEN is required to connect to IRAS.");
      }

      const response = await getInitialIrasTaxAgent(normalizedUen);
      if (response?.status === "success" && response?.data?.state != null) {
        persistIrasOAuthState(response.data.state);
        return {
          status: "success",
          message: response.message,
          data: {
            auth_url: response.data.authorization_url,
            authorization_url: response.data.authorization_url,
            state: response.data.state,
            expires_at: response.data.expires_at,
          },
        };
      }

      throw new Error(
        response?.message ||
          "Failed to initiate tax agent CorpPass authentication"
      );
    } catch (error) {
      console.error("Tax agent CorpPass authentication initiation failed:", error);
      throw error.error || error;
    }
  };

  // IRAS CorpPass Token Exchange (authorization code → server-stored CorpPass token)
  const exchangeCorpPassToken = async (code, state, redirectUri, uen) => {
    void redirectUri;
    const clearServerOauthHint = () => {
      sessionStorage.removeItem(
        IRAS_CONFIG.SECURITY_CONFIG.SERVER_OAUTH_CONNECTED_KEY
      );
      sessionStorage.removeItem(
        IRAS_CONFIG.SECURITY_CONFIG.SERVER_OAUTH_EXPIRES_AT_KEY
      );
    };

    try {
      const storedState = readPersistentValue(
        IRAS_CONFIG.SECURITY_CONFIG.STATE_STORAGE_KEY
      );
      if (storedState && state !== storedState) {
        throw new Error("State parameter mismatch - possible CSRF attack");
      }
      const normalizedUen = uen ? `${uen}`.trim() : "";
      if (!normalizedUen) {
        throw new Error("Company UEN is required to complete IRAS connection.");
      }

      clearServerOauthHint();
      const response = await exchangeIrasToken({
        code,
        state,
        uen: normalizedUen,
      });
      const nestedData =
        response?.data && typeof response.data === "object"
          ? response.data
          : null;
      const isSuccess =
        response?.status === "success" ||
        response?.success === true ||
        nestedData?.status === "success";

      if (isSuccess) {
        removePersistentValue(IRAS_CONFIG.SECURITY_CONFIG.STATE_STORAGE_KEY);
        const exp = nestedData?.expires_at || response?.expires_at;
        if (exp) {
          sessionStorage.setItem(
            IRAS_CONFIG.SECURITY_CONFIG.SERVER_OAUTH_EXPIRES_AT_KEY,
            String(exp)
          );
        }
        sessionStorage.setItem(
          IRAS_CONFIG.SECURITY_CONFIG.SERVER_OAUTH_CONNECTED_KEY,
          "1"
        );
        return {
          ...(response || {}),
          ...(nestedData || {}),
          status: "success",
        };
      }

      throw new Error(
        response?.message ||
          nestedData?.message ||
          "Failed to exchange CorpPass token"
      );
    } catch (error) {
      clearServerOauthHint();
      console.error("CorpPass token exchange failed:", error);
      const responseBody =
        error?.response?.data || error?.error?.response?.data || null;
      if (responseBody) {
        throw {
          ...responseBody,
          statusCode:
            error?.statusCode ||
            error?.response?.status ||
            error?.error?.response?.status,
        };
      }
      throw error;
    }
  };

  // IRAS Token Validation
  // TODO: Replace mockIrasAPI with actual IRAS API when subscription approved
  const validateIrasToken = async (accessToken) => {
    try {
      // DEMO: Using mock API for demonstration
      // PRODUCTION: Replace with actual IRAS API call
      const { mockIrasAPI } = await import("../services/mockIrasAPI");
      const response = await mockIrasAPI.validateIrasToken({
        access_token: accessToken,
      });
      return response;
    } catch (error) {
      console.error("IRAS token validation failed:", error);
      throw error.error || error;
    }
  };

  const submitGSTF5Return = async (gstData) => {
    try {
      const irasToken = localStorage.getItem("iras_access_token");
      if (!irasToken) {
        throw new Error("IRAS authentication required");
      }

      // Use mock API for demo
      const { mockIrasAPI } = await import("../services/mockIrasAPI");
      const response = await mockIrasAPI.submitGSTF5Return({
        iras_token: irasToken,
        gst_data: gstData,
      });

      return response;
    } catch (error) {
      console.error("GST F5 submission failed:", error);
      throw error.error || error;
    }
  };

  const getGSTSubmissions = async () => {
    try {
      // Use mock API for demo
      const { mockIrasAPI } = await import("../services/mockIrasAPI");
      const response = await mockIrasAPI.getGSTSubmissions();
      return response;
    } catch (error) {
      console.error("Failed to fetch GST submissions:", error);
      throw error.error || error;
    }
  };

  const getTaxSubmissions = async () => {
    try {
      // Use mock API for demo
      const { mockIrasAPI } = await import("../services/mockIrasAPI");
      const response = await mockIrasAPI.getTaxSubmissions();
      return response;
    } catch (error) {
      console.error("Failed to fetch tax submissions:", error);
      throw error.error || error;
    }
  };

  const getLatestPatchingUpdate = useCallback(async () => {
    try {
      return await patchingTemplateServices.getLatestPatchingSchedule();
    } catch (error) {
      console.error("Failed to fetch patching update:", error);
      throw error.error || error;
    }
  }, []);

  /** Disclosure */
  const listDisclosures = async (params) => {
    try {
      return await disclosureServices.listDisclosures(params);
    } catch (error) {
      console.error("Failed to list disclosures:", error);
      throw error.error || error;
    }
  };

  const getDisclosureById = async (d_id) => {
    try {
      return await disclosureServices.getDisclosureById(d_id);
    } catch (error) {
      console.error("Failed to get disclosure:", error);
      throw error.error || error;
    }
  };

  const createDisclosure = async (body) => {
    try {
      return await disclosureServices.createDisclosure(body);
    } catch (error) {
      console.error("Failed to create disclosure:", error);
      throw error.error || error;
    }
  };

  const updateDisclosure = async (d_id, body) => {
    try {
      return await disclosureServices.updateDisclosure(d_id, body);
    } catch (error) {
      console.error("Failed to update disclosure:", error);
      throw error.error || error;
    }
  };

  const deleteDisclosure = async (d_id) => {
    try {
      return await disclosureServices.deleteDisclosure(d_id);
    } catch (error) {
      console.error("Failed to delete disclosure:", error);
      throw error.error || error;
    }
  };

  const batchSyncDisclosures = async (body) => {
    try {
      return await disclosureServices.batchSyncDisclosures(body);
    } catch (error) {
      console.error("Failed to batch sync disclosures:", error);
      throw error.error || error;
    }
  };

  const deleteDisclosuresBatch = async (d_ids) => {
    try {
      return await disclosureServices.deleteDisclosuresBatch(d_ids);
    } catch (error) {
      console.error("Failed to batch delete disclosures:", error);
      throw error.error || error;
    }
  };

  /** DisclosureTag */
  const listDisclosureTags = async (params) => {
    try {
      return await disclosureServices.listDisclosureTags(params);
    } catch (error) {
      console.error("Failed to list disclosure tags:", error);
      throw error.error || error;
    }
  };

  const getDisclosureTagById = async (t_id) => {
    try {
      return await disclosureServices.getDisclosureTagById(t_id);
    } catch (error) {
      console.error("Failed to get disclosure tag:", error);
      throw error.error || error;
    }
  };

  const createDisclosureTag = async (body) => {
    try {
      return await disclosureServices.createDisclosureTag(body);
    } catch (error) {
      console.error("Failed to create disclosure tag:", error);
      throw error.error || error;
    }
  };

  const updateDisclosureTag = async (t_id, body) => {
    try {
      return await disclosureServices.updateDisclosureTag(t_id, body);
    } catch (error) {
      console.error("Failed to update disclosure tag:", error);
      throw error.error || error;
    }
  };

  const deleteDisclosureTag = async (t_id) => {
    try {
      return await disclosureServices.deleteDisclosureTag(t_id);
    } catch (error) {
      console.error("Failed to delete disclosure tag:", error);
      throw error.error || error;
    }
  };

  const deleteDisclosureTagsBatch = async (t_ids) => {
    try {
      return await disclosureServices.deleteDisclosureTagsBatch(t_ids);
    } catch (error) {
      console.error("Failed to batch delete disclosure tags:", error);
      throw error.error || error;
    }
  };

  const getCompanyPermission = async (target_u_id) => {
    try {
      const response = await api.get("/permission/getCompanyPermission", {
        params: {
          target_u_id,
        },
      });
      return response;
    } catch (error) {
      console.error("Failed to getCompanyPermission:", error);
      throw error.error || error;
    }
  };

  const assignPermissionToTaxComp = async (permissions) => {
    try {
      const response = await api.post(
        "/permission/assignPermissionToTaxComp",
        permissions
      );
      return response;
    } catch (error) {
      console.error("Failed to assign permission:", error);
      throw error.error || error;
    }
  };

  const getCompanyUserPermissionList = async () => {
    try {
      const response = await api.get(
        "/permission/getCompanyUserPermissionList"
      );
      return response;
    } catch (error) {
      console.error("Failed to get company user permission list:", error);
      throw error.error || error;
    }
  };

  const assignUsersToCompanyPermission = async ({ c_id, items }) => {
    try {
      const payload = {
        c_id,
        items,
      };

      const response = await api.post(
        "/permission/assignUsersToCompanyPermission",
        payload
      );
      return response;
    } catch (error) {
      console.error("Failed to assign users to company permission:", error);
      throw error.error || error;
    }
  };

  const getCompanyWithTaxComputationYears = async () => {
    try {
      const response = await api.get("/permission/getCompanyListForPermission");
      return response;
    } catch (error) {
      console.error("Failed to get company with tax computation years:", error);
      throw error.error || error;
    }
  };

  const getExchangeRate = async (
    currency,
    financialYearStart,
    financialYearEnd
  ) => {
    try {
      const response = await api.get("/data/exchange-rate", {
        params: {
          currency,
          financialYearStart,
          financialYearEnd,
        },
      });
      return response;
    } catch (wrapped) {
      console.error("Failed to get Mas currency rate:", wrapped);
      const axiosErr = wrapped?.error ?? wrapped;
      const responseData = axiosErr?.response?.data;
      const errorType =
        responseData?.errors?.errorType ?? responseData?.errorType ?? undefined;

      throw {
        error: axiosErr,
        errorType,
        statusCode: wrapped?.statusCode ?? axiosErr?.response?.status,
        message:
          responseData?.message ?? responseData?.error ?? wrapped?.message,
      };
    }
  };

  // Organization API methods
  const getOrganization = async () => {
    try {
      const res = await api.get("/organization");
      return res.data;
    } catch (error) {
      console.error("Failed to get organization:", error);
      throw error.error || error;
    }
  };

  const createOrganization = async (values) => {
    try {
      const res = await api.post("/organization", values);
      return res.data;
    } catch (error) {
      console.error("Failed to create organization:", error);
      throw error.error || error;
    }
  };

  const updateOrganization = async (values) => {
    try {
      const res = await api.post("/organization/update", values);
      return res.data;
    } catch (error) {
      console.error("Failed to update organization:", error);
      throw error.error || error;
    }
  };

  const deleteOrganization = async () => {
    try {
      const res = await api.delete("/organization");
      return res.data;
    } catch (error) {
      console.error("Failed to delete organization:", error);
      throw error.error || error;
    }
  };

  const handleCreateOrganization = async (formData) => {
    setCreateOrgLoading(true);
    try {
      const result = await createOrganization(formData);
      setOrganizationData(result);
      setCreateOrgDialogOpen(false);
      setCreateOrgLoading(false);
      // Reload to update the app state
      window.location.reload();
    } catch (error) {
      setCreateOrgLoading(false);
      console.error("Error creating organization:", error);
      alert("Failed to create organization. Please try again.");
    }
  };

  const checkAccess = useCallback(
    (action, module = "", options = {}) => {
      const { user_type = "", user_role = "" } = global_authState || {};

      const hasCompanyRoleOption = Object.prototype.hasOwnProperty.call(
        options,
        "companyRole"
      );
      const hasRegisteredCompanyRole = Boolean(registeredCompanyRole);
      const companyRole = hasCompanyRoleOption
        ? options.companyRole
        : registeredCompanyRole && typeof registeredCompanyRole === "object"
          ? registeredCompanyRole.role
          : registeredCompanyRole;

      const userType = user_type?.toLowerCase() || "";
      const normalizedUserRole = user_role?.toLowerCase() || "admin";
      const normalizedCompanyRole =
        companyRole && String(companyRole).toLowerCase();

      const isSystemModule = SYSTEM_CONTROL.includes(module);
      const isCompanyScopedModule = [TAX_COMPUTATION].includes(module);
      const shouldUseCompanyRole =
        isCompanyScopedModule &&
        (hasCompanyRoleOption || hasRegisteredCompanyRole);

      // decide which role to use
      const effectiveRole = shouldUseCompanyRole
        ? normalizedCompanyRole || "viewer"
        : isSystemModule
          ? normalizedUserRole
          : normalizedCompanyRole || normalizedUserRole || "admin";

      // console.log("[checkAccess] roles input:", {
      //   isSystemModule,
      //   effectiveRole,
      //   normalizedUserRole,
      //   normalizedCompanyRole,
      //   normalizedUserRole,
      //   module,
      //   action
      // });

      const ACCESS_MATRIX = {
        types: {
          admin: true,
          root: true,
        },
        roles: {
          admin: (act, mod) => mod !== SETTING_BILLING,

          editor: (act, mod) => {
            const restricted = [
              ...PANEL_EDITOR_CONTROL,
              SETTING_ORGANIZATION,
              SETTING_BILLING,
            ];

            if (
              [TAX_COMPUTATION, PANEL_COMPANY_MANAGEMENT].includes(mod) &&
              act !== "delete"
            )
              return true;

            return (
              (act === "view" || act === "create" || act === "edit") &&
              !restricted.includes(mod)
            );
          },

          viewer: (act, mod) => {
            const notAllowedInViewer = [
              ...PANEL_EDITOR_CONTROL,
              SETTING_BILLING,
              SETTING_ORGANIZATION,
            ];
            return act === "view" && !notAllowedInViewer.includes(mod);
          },
        },
      };

      if (!shouldUseCompanyRole && ACCESS_MATRIX.types[userType]) {
        return true;
      }

      const roleHandler = ACCESS_MATRIX.roles[effectiveRole];
      if (roleHandler) {
        return roleHandler(action, module);
      }

      return false;
    },
    [global_authState, registeredCompanyRole]
  );

  if (isLoading) {
    return <Loading />;
  }

  return (
    <AuthContext.Provider
      value={{
        setIsLoading,
        isLoading,
        login,
        startMicrosoftSSO,
        completeMicrosoftSSOCallback,
        register,
        verifyUser,
        registerSubUser,
        registerLocalSubUser,
        registerSubsidiaryUser,
        verifySubsidiaryUser,
        convertSubUsertoSubsidiary,
        updateSubsidiaryUserInfo,
        updateSubsidiaryUserExpireDate,
        terminateSubsidiaryUser,
        verifySubUser,
        updateSubUserRole,
        updateSubUserName,
        removeSubUser,
        transferOwnership,
        activeSubscription,
        terminateSubscription,
        renewSubscription,
        updateSubscriptionDates,
        resetSubscriptionToTrial,
        chargeSavedCard,
        updateUserSubscription,
        getAllUserWithSubscription,
        getPlans,
        getPaymentBySubscriptionId,
        getNextBillingPayment,
        getSubscriptionChangeLog,
        changePassword,
        forgotPassword,
        resetPassword,
        logout: initiateLogout,
        switchAccountForVerification,
        cancel_verify: cancelLogout,
        resendVerificationEmail,
        resendSubsidiaryVerificationEmail,
        resendSubsidiaryAuthEmail,
        google_auth,
        google_auth_challenge,
        reset_google_auth,
        reset_google_auth_challenge,
        verify_google_auth,
        verify_login,
        refresh_google_auth,
        removeRootUser,
        getUserData,
        updateUserProfile,
        getSubUserListByUID,
        getSubsidiaryUserListByUID,
        getSubUserList,
        getSubsidiaryUserList,
        createCompany,
        updateCompany,
        updateCaSettings,
        createTaxComputation,
        updateTaxComputation,
        deleteTaxComputation,
        updateDetailsPL,
        updateMainComputation,
        rollOverTaxComputation,
        reorderScheduleNo,
        reorderCAScheduleNo,
        getCompanyList,
        getCompanyListByUID,
        getTaxComputationList,
        getDetailsPLData,
        getMainComputationData,
        getMainCompDocOnly,
        getStatementDoc,
        getCASettings,
        getScheduleData,
        getSchedules,
        getSchedulesWithSavedDoc,
        getCaSchedulesWithSavedDoc,
        getCAScheduleList,
        getCAScheduleDataByType,
        getCASummaryPDFDetails,
        global_authState,
        global_dataState,
        authBootstrapComplete,
        refreshAuthenticatedUser,
        isTwoFaSetupPending,
        readAuthVerificationSession,
        updateAuthVerificationSession,
        removeAuthVerificationSession,
        showQRCodeDialog,
        deleteCompany,
        tempDeleteCompany,
        restoreCompany,
        createPLSchedule,
        updatePLSchedule,
        createCASchedule,
        updateCASchedule,
        addAutoloadData,
        editAutoloadData,
        deleteAutoloadData,
        resetData,
        resetAllData,
        setData,
        deleteScheduleByScheduleType,
        removeRowFromSchedule,
        deleteCAScheduleByType,
        checkAccess,
        currentCompanyRole,
        registerCompanyRole,
        clearCompanyRole,
        getCompanyRole,
        // IRAS Integration Functions
        initiateCorpPassAuth,
        initiateCorpPassTaxAgentAuth,
        exchangeCorpPassToken,
        validateIrasToken,
        submitGSTF5Return,
        getGSTSubmissions,
        getTaxSubmissions,
        clearIrasCredentials,
        getLatestPatchingUpdate,
        listDisclosures,
        getDisclosureById,
        createDisclosure,
        updateDisclosure,
        deleteDisclosure,
        batchSyncDisclosures,
        deleteDisclosuresBatch,
        listDisclosureTags,
        getDisclosureTagById,
        createDisclosureTag,
        updateDisclosureTag,
        deleteDisclosureTag,
        deleteDisclosureTagsBatch,
        getCompanyPermission,
        assignPermissionToTaxComp,
        assignUsersToCompanyPermission,
        getCompanyUserPermissionList,
        getExchangeRate,
        getCompanyWithTaxComputationYears,
        getOrganization,
        createOrganization,
        updateOrganization,
        deleteOrganization,
        organizationData,
      }}
    >
      {children}
      <LogoutDialog
        open={logoutDialogOpen}
        onClose={cancelLogout}
        onConfirm={handleLogout}
        loading={logoutLoading}
      />
      <SessionExpiredDialog
        open={sessionExpiredDialogOpen}
        onConfirm={handleSessionExpired}
      />
      <TwoFaExpiredDialog
        open={twoFaExpiredDialogOpen}
        onConfirm={handleTwoFaExpiredConfirm}
      />
      <ServiceErrorDialog
        open={serviceErrorDialogOpen}
        onConfirm={() => setServiceErrorDialogOpen(false)}
      />
      <ExpiredAccountDialog
        open={expiredAccountDialogOpen}
        onConfirm={() => setExpiredAccountDialogOpen(false)}
      />
      <RefreshTokenExpiringDialog
        open={refreshExpiringDialogOpen}
        minutesLeft={refreshExpiringMinutesLeft}
        onConfirm={handleDismissRefreshExpiring}
      />
      <CreateOrganizationDialog
        open={createOrgDialogOpen}
        onSubmit={handleCreateOrganization}
        loading={createOrgLoading}
      />
    </AuthContext.Provider>
  );
};

export default AuthProvider;
